<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {computed, ref, watch} from "vue";
import {useMutation, useQuery} from "@tanstack/vue-query";
import axios from "axios";
import {errorToast, successToast} from "@/composables/useToasts";
import StepBox from "@/components/google/partials/StepBox.vue";
import RadioBox from "@/components/google/partials/RadioBox.vue";
import RadioBoxGrid from "@/components/google/partials/RadioBoxGrid.vue";
import PlaceholderRadioBox from "@/components/google/partials/PlaceholderRadioBox.vue";
import SearchInput from "@/components/global-themed/SearchInput.vue";

const props = defineProps({
  accountsJson: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    required: true,
  },
  websiteUrl: {
    type: String,
    required: true,
  },
  connectedSelectionsJson: {
    type: String,
    default: '[]',
  },
});

const selectedAccountId = ref();
const selectedLocationId = ref();
const locationSearch = ref('');
const accounts = computed(() => JSON.parse(props.accountsJson));
const connectedSelections = computed(() => JSON.parse(props.connectedSelectionsJson));

const isAccountConnected = (accountId) => connectedSelections.value.some(
  (selection) => selection.accountId === accountId,
);

const isLocationConnected = (accountId, locationId) => connectedSelections.value.some(
  (selection) => selection.accountId === accountId && selection.locationId === locationId,
);

watch(accounts, () => {
  if (selectedAccountId.value) {
    return;
  }

  const connectedAccount = connectedSelections.value
    .map((selection) => accounts.value.find((account) => account.id === selection.accountId))
    .find(Boolean);

  if (connectedAccount) {
    selectedAccountId.value = connectedAccount.id;
    return;
  }

  if (accounts.value.length === 1) {
    selectedAccountId.value = accounts.value[0].id;
  }
}, {
  immediate: true,
});

watch(selectedAccountId, () => {
  selectedLocationId.value = undefined;
  locationSearch.value = '';
});

const locationsQuery = useQuery({
  queryKey: computed(() => ['google-business-locations', selectedAccountId.value]),
  queryFn: async () => {
    const {data} = await axios.get('/ajax/v2/google/business/locations', {
      params: {
        accountId: selectedAccountId.value,
      },
    });

    return data;
  },
  enabled: computed(() => !!selectedAccountId.value),
});

const locations = computed(() => locationsQuery.data.value?.locations ?? []);

const filteredLocations = computed(() => {
  const search = locationSearch.value.trim().toLowerCase();

  if (!search) {
    return locations.value;
  }

  const normalizedSearch = search.replace(/\s+/g, '');

  return locations.value.filter((location) => {
    const title = location.title?.toLowerCase() ?? '';
    const postalCode = location.postalCode?.toLowerCase() ?? '';
    const normalizedPostalCode = postalCode.replace(/\s+/g, '');

    return title.includes(search)
      || postalCode.includes(search)
      || normalizedPostalCode.includes(normalizedSearch);
  });
});

watch(filteredLocations, (locationsList) => {
  if (!selectedLocationId.value) {
    return;
  }

  const selectedLocationIsVisible = locationsList.some(
    (location) => location.id === selectedLocationId.value,
  );

  if (!selectedLocationIsVisible) {
    selectedLocationId.value = undefined;
  }
});

const formatWebsiteUri = (websiteUri) => {
  if (!websiteUri) {
    return null;
  }

  try {
    const url = new URL(websiteUri.includes('://') ? websiteUri : `https://${websiteUri}`);

    return url.hostname;
  } catch {
    return websiteUri.replace(/^https?:\/\//, '').split('/')[0];
  }
};

const {mutate: submit, isPending, isSuccess} = useMutation({
  mutationFn: () => axios.post('/ajax/v2/google/business', {
    accountId: selectedAccountId.value,
    locationId: selectedLocationId.value,
  }),
  onSuccess: ({data}) => {
    successToast('Google Business Profile connected successfully, redirecting...');
    window.setTimeout(() => {
      window.location.href = data.redirect;
    }, 2000);
  },
  onError: () => {
    errorToast('Something went wrong, please try again');
  },
});

</script>

<template>
  <div class="tw-flex tw-flex-col tw-gap-y-8 tw-px-4 md:tw-px-0">
    <div>
      <h1 class="tw-font-bold tw-text-3xl md:tw-text-4.5xl">
        Connect Google Business Profile
      </h1>
      <p class="tw-text-lg">
        Signed in as <span class="tw-font-bold">{{ email }}</span>, please choose what to link to <span
          class="tw-font-bold"
        >{{ websiteUrl }}</span>
      </p>
    </div>
    <StepBox
      :step="1"
      title="Choose account"
      :active="!selectedAccountId"
    >
      <template #subtitle>
        <p>
          Choose which Google Business Profile account you would like to connect:
        </p>
      </template>

      <template #default>
        <RadioBoxGrid>
          <RadioBox
            v-for="account in accounts"
            :key="account.id"
            v-model="selectedAccountId"
            :title="account.displayName"
            :value="account.id"
            :already-connected="isAccountConnected(account.id)"
          >
            <p class="tw-text-neutral-700">
              Account ID: {{ account.id }}
            </p>
          </RadioBox>
        </RadioBoxGrid>
      </template>
    </StepBox>

    <StepBox
      :step="2"
      title="Select location"
      :active="!!selectedAccountId"
    >
      <template #subtitle>
        <p>
          Choose which location to link to your website:
        </p>
      </template>

      <template #default>
        <div
          v-if="locations.length > 0"
          class="tw-mb-4"
        >
          <SearchInput
            v-model="locationSearch"
            placeholder="Search by name or postcode"
          />
        </div>

        <RadioBoxGrid v-show="filteredLocations.length > 0">
          <RadioBox
            v-for="location in filteredLocations"
            :key="location.id"
            v-model="selectedLocationId"
            :title="location.title"
            :value="location.id"
            :already-connected="isLocationConnected(selectedAccountId, location.id)"
          >
            <div class="tw-flex tw-flex-col tw-gap-1">
              <div v-if="location.addressLines?.length || location.locality || location.postalCode">
                <p
                  v-for="(addressLine, index) in location.addressLines"
                  :key="`${location.id}-address-${index}`"
                  class="tw-text-neutral-700 tw-leading-tight"
                >
                  {{ addressLine }}
                </p>
                <p
                  v-if="location.locality"
                  class="tw-text-neutral-700 tw-leading-tight"
                >
                  {{ location.locality }}
                </p>
                <p
                  v-if="location.postalCode"
                  class="tw-text-neutral-700 tw-leading-tight"
                >
                  {{ location.postalCode }}
                </p>
              </div>
              <p
                v-if="location.websiteUri"
                class="tw-text-neutral-700 tw-break-all"
              >
                {{ formatWebsiteUri(location.websiteUri) }}
              </p>
              <p
                v-if="location.primaryPhone"
                class="tw-text-neutral-700"
              >
                {{ location.primaryPhone }}
              </p>
            </div>
          </RadioBox>
        </RadioBoxGrid>

        <p
          v-if="locations.length > 0 && filteredLocations.length === 0"
          class="tw-text-neutral-700"
        >
          No locations match your search.
        </p>

        <RadioBoxGrid v-show="!locations.length">
          <PlaceholderRadioBox />
          <PlaceholderRadioBox />
        </RadioBoxGrid>

        <div class="tw-flex tw-flex-col-reverse sm:tw-flex-row tw-gap-2 tw-mt-8">
          <DefaultButton
            as="a"
            href="/google/oauth/cancel"
            class="sm:tw-mr-auto"
            variant="base"
          >
            Cancel
          </DefaultButton>
          <DefaultButton
            class="sm:tw-ml-auto"
            :disabled="!selectedAccountId || !selectedLocationId || isPending || isSuccess"
            :loading="isPending"
            @click="submit"
          >
            Connect
          </DefaultButton>
        </div>
      </template>
    </StepBox>
  </div>
</template>
